// achievements.js — badges computed deterministically from workout history.
//
// Nothing is stored: one chronological pass over completed sessions yields every
// metric and the session in which each badge was first earned, so the same
// history always gives the same badges (and edits/deletes re-derive them).
//
//   achievementDefs(units)                 → the badge catalogue
//   computeAchievements(state, now)        → { list, earned, total, bySession }
//   achievementsEarnedIn(state, sessionId) → badges first earned in that workout
//   newAchievementsForSession(stateBefore, stateAfter | session) → newly earned badges
//
// Badge: { id, cat, title, desc, icon, tier (1 bronze … 4 platinum), target,
//          value, progress 0..1, earned, earnedAt (ms), sessionId, fmt(v) }

import { completedSessions, e1rm, isWorkingSet, indexExercises, musclesFor, weekStart } from './analytics.js';
import { liftKeyFor } from './standards.js';

const DAY = 86400000;
const WEEK = 7 * DAY;
const KG = 0.45359237;
const MAIN = ['Chest', 'Back', 'Shoulders', 'Quads', 'Hamstrings', 'Glutes', 'Calves', 'Biceps', 'Triceps', 'Core'];

export const ACH_CATEGORIES = [
  { id: 'milestone', label: 'Milestones' },
  { id: 'consistency', label: 'Consistency' },
  { id: 'strength', label: 'Strength' },
  { id: 'volume', label: 'Volume' },
  { id: 'records', label: 'Records' },
  { id: 'craft', label: 'Craft' },
];

const n0 = (v) => Math.round(v).toLocaleString('en-US');
const compact = (v) => (v >= 1e6 ? `${+(v / 1e6).toFixed(v >= 1e7 ? 0 : 1)}M` : v >= 1000 ? `${+(v / 1000).toFixed(v >= 1e5 ? 0 : 1)}k` : String(Math.round(v)));

export function achievementDefs(units = 'lb') {
  const kg = units === 'kg';
  const u = kg ? 'kg' : 'lb';
  const defs = [];
  const add = (d) => defs.push({ fmt: n0, ...d });

  // Milestones — workouts logged
  [[1, 'First workout', 'Log your first workout', 1], [10, 'Ten down', 'Finish 10 workouts', 1], [25, 'Regular', 'Finish 25 workouts', 2],
    [50, 'Fifty', 'Finish 50 workouts', 2], [100, 'Century', 'Finish 100 workouts', 3], [250, 'Iron habit', 'Finish 250 workouts', 4]]
    .forEach(([t, title, desc, tier]) => add({ id: `workouts_${t}`, cat: 'milestone', icon: 'medal', title, desc, tier, target: t, metric: 'workouts' }));

  // Consistency — weeks in a row with at least one workout
  [[4, 'Four-week streak', 1], [12, 'Twelve-week streak', 2], [26, 'Half-year streak', 3], [52, 'Year-long streak', 4]]
    .forEach(([t, title, tier]) => add({ id: `streak_${t}`, cat: 'consistency', icon: 'flame', title, desc: `Train at least once a week, ${t} weeks in a row`, tier, target: t, metric: 'streak', fmt: (v) => `${v} wk` }));
  add({ id: 'month_12', cat: 'consistency', icon: 'calendar', title: 'Big month', desc: '12 workouts in one calendar month', tier: 2, target: 12, metric: 'bestMonth' });
  add({ id: 'months_3x8', cat: 'consistency', icon: 'calendar', title: 'Steady quarter', desc: '3 months in a row with 8+ workouts each', tier: 3, target: 3, metric: 'steadyMonths', fmt: (v) => `${v} mo` });
  add({ id: 'comeback', cat: 'consistency', icon: 'return', title: 'Comeback', desc: 'Return to training after 3+ weeks off', tier: 1, target: 1, metric: 'comebacks' });
  add({ id: 'early_10', cat: 'consistency', icon: 'sun', title: 'Early riser', desc: '10 workouts started before 7 am', tier: 2, target: 10, metric: 'early' });
  add({ id: 'program_done', cat: 'consistency', icon: 'flag', title: 'Program finisher', desc: 'Complete a full program block', tier: 3, target: 1, metric: 'programsDone' });

  // Strength — plate milestones on the big three (heaviest working set)
  const plates = kg ? [[1, 60], [2, 100], [3, 140], [4, 180]] : [[1, 135], [2, 225], [3, 315], [4, 405]];
  const word = ['', 'One', 'Two', 'Three', 'Four'];
  for (const [lift, name] of [['bench', 'bench'], ['squat', 'squat'], ['deadlift', 'deadlift']]) {
    for (const [p, w] of plates) {
      const tier = Math.min(4, p + (lift === 'bench' ? 1 : 0));
      add({ id: `plate_${lift}_${p}`, cat: 'strength', icon: 'plate', plates: p, title: `${word[p]}-plate ${name}`,
        desc: `${name[0].toUpperCase() + name.slice(1)} ${w} ${u} for a working set`, tier, target: w, metric: `max_${lift}`, fmt: (v) => `${n0(v)} ${u}` });
    }
  }

  // Volume — lifetime and single-session tonnage, in the user's units
  const life = kg ? [[25e3, 1], [100e3, 2], [500e3, 3], [2.5e6, 4]] : [[50e3, 1], [250e3, 2], [1e6, 3], [5e6, 4]];
  life.forEach(([t, tier]) => add({ id: `volume_${t}`, cat: 'volume', icon: 'stack', title: `${compact(t)} ${u} lifted`, desc: `Lift ${compact(t)} ${u} in total across all workouts`, tier, target: t, metric: 'volume', fmt: (v) => `${compact(v)} ${u}` }));
  const big = kg ? 10e3 : 20e3;
  add({ id: 'big_day', cat: 'volume', icon: 'bolt', title: 'Big day', desc: `Move ${compact(big)} ${u} in a single workout`, tier: 2, target: big, metric: 'bestSession', fmt: (v) => `${compact(v)} ${u}` });
  add({ id: 'sets_1000', cat: 'volume', icon: 'stack', title: 'A thousand sets', desc: 'Log 1,000 working sets', tier: 3, target: 1000, metric: 'sets' });

  // Records — PRs (an exercise's best estimated 1RM beaten)
  [[1, 'First PR', 1], [10, 'Ten PRs', 2], [50, 'Fifty PRs', 3], [100, 'Record breaker', 4]]
    .forEach(([t, title, tier]) => add({ id: `prs_${t}`, cat: 'records', icon: 'trophy', title, desc: t === 1 ? 'Beat an exercise\'s best estimated 1RM' : `Set ${t} personal records`, tier, target: t, metric: 'prs' }));

  // Craft — habits that make the data (and the coaching) better
  add({ id: 'variety_25', cat: 'craft', icon: 'grid', title: 'Explorer', desc: 'Log 25 different exercises', tier: 2, target: 25, metric: 'distinct' });
  add({ id: 'rpe_100', cat: 'craft', icon: 'gauge', title: 'Effort tracker', desc: 'Rate the effort (RPE) of 100 sets', tier: 2, target: 100, metric: 'rpeSets' });
  add({ id: 'balanced_week', cat: 'craft', icon: 'body', title: 'Well-rounded', desc: 'Hit all 10 major muscle groups with 2+ hard sets in one week', tier: 3, target: 10, metric: 'bestWeekMuscles', fmt: (v) => `${v}/10` });
  return defs;
}

// ——— The pass ——————————————————————————————————————————————————————————————

function programTarget(p) {
  const weeks = (p.cycle && !p.cycle.repeat && p.cycle.weeks?.length) || p.weeks || 8;
  const days = Math.max(1, (p.days || []).length);
  return { weeks, sessions: Math.ceil(weeks * days * 0.75) };
}

export function computeAchievements(state, now = Date.now()) {
  const units = state.prefs?.units === 'kg' ? 'kg' : 'lb';
  const defs = achievementDefs(units);
  const idx = indexExercises(state);
  const timed = new Set((state.exercises || []).filter(e => e.unit === 'sec').map(e => e.id));
  const liftOf = {};
  const lk = (id) => (id in liftOf ? liftOf[id] : (liftOf[id] = liftKeyFor(idx[id] || { id })));
  const toU = (lb) => (units === 'kg' ? lb * KG : lb);
  const programs = Object.fromEntries((state.programs || []).map(p => [p.id, { ...programTarget(p), weeksSeen: new Set(), count: 0, done: false }]));

  const m = {
    workouts: 0, streak: 0, bestMonth: 0, steadyMonths: 0, comebacks: 0, early: 0, programsDone: 0,
    max_bench: 0, max_squat: 0, max_deadlift: 0, volume: 0, bestSession: 0, sets: 0, prs: 0,
    distinct: 0, rpeSets: 0, bestWeekMuscles: 0,
  };
  const earned = {};
  const weeks = new Set();
  const months = {};
  const best = {};
  const seenEx = new Set();
  const weekMuscles = {};
  let prevT = null;
  let maxStreak = 0;
  let maxSteady = 0;

  const check = (s) => {
    for (const d of defs) {
      if (earned[d.id]) continue;
      if ((m[d.metric] || 0) >= d.target - 1e-9) earned[d.id] = { at: s.completedAt || s.startedAt, sessionId: s.id };
    }
  };

  for (const s of completedSessions(state)) {
    if (s.startedAt > now) continue;
    m.workouts++;
    // streak (weeks with ≥ 1 workout, ending at this workout's week)
    const wk = weekStart(s.startedAt);
    weeks.add(wk);
    let st = 0;
    for (let w = wk; weeks.has(w); w = weekStart(w - 3 * DAY)) st++;
    maxStreak = Math.max(maxStreak, st);
    m.streak = maxStreak;
    // months
    const d = new Date(s.startedAt);
    const mk = d.getFullYear() * 12 + d.getMonth();
    months[mk] = (months[mk] || 0) + 1;
    m.bestMonth = Math.max(m.bestMonth, months[mk]);
    let run = 0;
    for (let k = mk; (months[k] || 0) >= 8; k--) run++;
    maxSteady = Math.max(maxSteady, run);
    m.steadyMonths = maxSteady;
    if (prevT != null && s.startedAt - prevT >= 21 * DAY) m.comebacks++;
    prevT = s.startedAt;
    if (d.getHours() < 7) m.early++;

    // sets, volume, PRs, plates, muscles
    let vol = 0;
    const sessionBest = {};
    const wm = (weekMuscles[wk] = weekMuscles[wk] || {});
    for (const x of s.sets || []) {
      if (!isWorkingSet(x)) continue;
      m.sets++;
      if (x.rpe != null && x.rpe > 0) m.rpeSets++;
      if (x.exerciseId) seenEx.add(x.exerciseId);
      for (const mu of musclesFor(idx[x.exerciseId]).primary) if (MAIN.includes(mu)) wm[mu] = (wm[mu] || 0) + 1;
      if (timed.has(x.exerciseId) || !(x.w > 0)) continue;
      vol += x.w * x.reps;
      const e = e1rm(x.w, x.reps);
      if (!sessionBest[x.exerciseId] || e > sessionBest[x.exerciseId]) sessionBest[x.exerciseId] = e;
      const key = lk(x.exerciseId);
      if (key === 'bench' || key === 'squat' || key === 'deadlift') m['max_' + key] = Math.max(m['max_' + key], toU(x.w));
    }
    for (const [id, e] of Object.entries(sessionBest)) {
      if (best[id] != null && e > best[id] * 1.001) m.prs++;
      best[id] = Math.max(best[id] || 0, e);
    }
    m.volume += toU(vol);
    m.bestSession = Math.max(m.bestSession, toU(vol));
    m.distinct = seenEx.size;
    m.bestWeekMuscles = Math.max(m.bestWeekMuscles, MAIN.filter(mu => (wm[mu] || 0) >= 2).length);
    // program blocks
    const p = programs[s.programId];
    if (p && !p.done) {
      p.count++;
      p.weeksSeen.add(wk);
      if (p.weeksSeen.size >= p.weeks && p.count >= p.sessions) { p.done = true; m.programsDone++; }
    }
    check(s);
  }

  const list = defs.map(dft => {
    const value = m[dft.metric] || 0;
    const e = earned[dft.id];
    return { ...dft, value, progress: Math.max(0, Math.min(1, value / dft.target)), earned: !!e, earnedAt: e ? e.at : null, sessionId: e ? e.sessionId : null };
  });
  const bySession = {};
  for (const a of list) if (a.sessionId) (bySession[a.sessionId] = bySession[a.sessionId] || []).push(a);
  return { list, earned: list.filter(a => a.earned).length, total: list.length, bySession, metrics: m };
}

export function achievementsEarnedIn(state, sessionId, now = Date.now()) {
  return computeAchievements(state, now).bySession[sessionId] || [];
}

// newAchievementsForSession(stateBefore, stateAfter) — badges earned in after but not before.
// newAchievementsForSession(state, session) — badges first earned by that session
// (the session is added to the history if it is not there yet, e.g. still live).
export function newAchievementsForSession(stateBefore, x, now = Date.now()) {
  if (!stateBefore || !x) return [];
  if (Array.isArray(x.sessions)) {
    const had = new Set(computeAchievements(stateBefore, now).list.filter(a => a.earned).map(a => a.id));
    return computeAchievements(x, now).list.filter(a => a.earned && !had.has(a.id));
  }
  const ses = { ...x, completedAt: x.completedAt || now };
  const sessions = [...(stateBefore.sessions || []).filter(s => s.id !== ses.id), ses];
  return achievementsEarnedIn({ ...stateBefore, sessions }, ses.id, Math.max(now, ses.completedAt));
}

// The closest unearned badges (highest progress first), for "Next up".
export function nextAchievements(result, limit = 3) {
  return result.list.filter(a => !a.earned && a.progress > 0)
    .sort((a, b) => b.progress - a.progress || a.tier - b.tier)
    .slice(0, limit);
}
